<script setup lang="ts">
import { getCreditCardIssuer } from "@/builder/BuilderUtilities";
import ValidatedInput from "@/builder/components/input/ValidatedInput.vue";
import { FieldStyle, PartialDisplayType } from "@/builder/enums";
import { PageDesign } from "@/builder/interfaces/";
import { useRenderStore } from "@/builder/stores/renderStore";
import RadioButton from "@/components/shared/form/radio/RadioButton.vue";
import { MerchantType, PaymentMethodType, PaymentMethod } from "@/enums";
import { vMaska } from "maska/vue";
import { computed, onBeforeUnmount, onMounted, PropType, ref, watch } from "vue";
import { IPaymentComponent } from "./PaymentInterface";

// injects
const renderStore = useRenderStore();

const fieldStyle = computed(() => {
    return props.pageDesign.fieldStyle;
});

// props
const props = defineProps({
    component: {
        type: Object as PropType<IPaymentComponent>,
        required: true,
    },
    organizationId: {
        type: Number as PropType<number | null>,
        required: true,
        default: null,
    },
    merchantAccount: {
        type: String,
        required: true,
    },
    tokenizationKey: {
        type: String,
        required: true,
    },
    readOnly: {
        type: Boolean,
        required: true,
        default: false,
    },
    pageDesign: {
        type: Object as PropType<PageDesign>,
        required: true,
    },
    formIsOnParentStep: Boolean,
});

// refs
const isAuthNet = ref(
    renderStore.page.merchantType == MerchantType.AuthorizeNet
);
const scriptLoaded = ref(false);

// computed
const testMode = computed(
    () => renderStore.environment !== "production" || renderStore.isTestMode
);

const accountTypes: Array<{ name: string; value: string }> = [
    {
        name: "Checking",
        value: "checking",
    },
    {
        name: "Savings",
        value: "savings",
    },
    {
        name: "Business Checking",
        value: "businessChecking",
    },
];

const isModalDisplay = computed(() => {
    return renderStore.page.partialDisplayType === PartialDisplayType.Modal;
})

onMounted(async () => {
    loadAuthNetScript();

    if (!renderStore.gift.paymentMethodType)
        renderStore.gift.paymentMethodType = PaymentMethodType.Credit;
});

onBeforeUnmount(() => {
    //remove stripe script
    const script = document.getElementById("authnet-js");
    if (script) {
        script.remove();
    }

    //find stripe script via src
    const scriptUrl = testMode.value
        ? "https://jstest.authorize.net/v1/Accept.js"
        : "https://js.authorize.net/v1/Accept.js";
    const scriptSrc = document.querySelector(`script[src="${scriptUrl}"]`);
    if (scriptSrc) {
        scriptSrc.remove();
    }

    scriptLoaded.value = false;
});

const loadAuthNetScript = () => {
    if (!props.formIsOnParentStep && !props.readOnly) return;
    if (!isAuthNet.value) return;
    if (typeof window === "undefined") return;
    const scriptUrl = testMode.value
        ? "https://jstest.authorize.net/v1/Accept.js"
        : "https://js.authorize.net/v1/Accept.js";

    const script = document.createElement("script");
    script.id = "authnet-js";
    script.src = scriptUrl;
    script.async = true;
    script.defer = true;
    script.onload = () => {
        scriptLoaded.value = true;
    };
    document.head.appendChild(script);
};

const cardNumberChanged = async () => {
    await renderStore.tokenizePaymentMethod();
    renderStore.gift.creditCardType = getCreditCardIssuer(
        renderStore.anetPayment.cardNumber
    );
};

const paymentMethodTypeChanged = () => {
    renderStore.anetPayment = {
        accountNumber: "",
        accountType: "",
        cardCode: "",
        cardNumber: "",
        expDate: "",
        routingNumber: "",
    };
};

watch(
    () => props.formIsOnParentStep,
    async (newVal) => {
        loadAuthNetScript();
    }
);
</script>
<template>
    <div class="form-radio-group rd-inline-flex rd-leading-none" :class="isModalDisplay ? 'rd-mb-1.5' : 'rd-mb-3.5'">
        <RadioButton id="creditCard" :selected="renderStore.gift.paymentMethodType === PaymentMethodType.Credit"
            :value="PaymentMethodType.Credit" name="creditCard" v-model="renderStore.gift.paymentMethodType"
            labelText="<i class='rd-mr-1.5 fa-solid fa-credit-card'></i> Card"
            label-classes="rd-items-center rd-inline-flex rd-leading-none" :is-admin="false" :is-inline="true"
            @update:modelValue="paymentMethodTypeChanged" />

        <RadioButton
            v-if="props.component.customData.paymentMethods.includes(PaymentMethod.US_Bank_Account)"
            id="ach"
            :selected="renderStore.gift.paymentMethodType === PaymentMethodType.ACH"
            :value="PaymentMethodType.ACH" name="ach" v-model="renderStore.gift.paymentMethodType"
            labelText="<i class='rd-mr-1.5 fa-solid fa-building-columns'></i> US Bank Account"
            label-classes="rd-items-center rd-inline-flex rd-leading-none" :is-admin="false" :is-inline="true"
            class="rd-ml-5" @update:modelValue="paymentMethodTypeChanged" />
    </div>

    <div v-if="renderStore.gift.paymentMethodType === PaymentMethodType.Credit"
        :class="{ 'fields-compact': fieldStyle == FieldStyle.Compact }">

        <div class="form-row">
            <ValidatedInput id="cardNumber" :value="renderStore.anetPayment.cardNumber" label="Card number"
                label-for="cardNumber" name="cardNumber" class="rd-w-full" :isRequired="true">
                <template v-slot:input>
                    <input id="cardNumber" name="cardNumber" type="text" v-model="renderStore.anetPayment.cardNumber"
                        placeholder="1234 1234 1234 1234" :maxLength="16" @change="cardNumberChanged"
                        @blur="cardNumberChanged" />
                </template>
            </ValidatedInput>
        </div>

        <div class="form-row" :class="[{ 'rd-mt-0 md:rd-gap-x-4 md:rd-mt-4': fieldStyle != FieldStyle.Compact && !isModalDisplay },
        { 'rd-mt-0 md:rd-gap-x-4': (fieldStyle != FieldStyle.Compact && isModalDisplay) }]">

            <ValidatedInput id="expDate" :value="renderStore.anetPayment.expDate" label="Expiration date"
                label-for="expDate" name="expDate" class="rd-w-full md:rd-flex-equal" :class="[{ 'rd-mt-4 md:rd-mt-0': fieldStyle != FieldStyle.Compact && !isModalDisplay },
                { 'rd-mt-2.5': (fieldStyle != FieldStyle.Compact && isModalDisplay) }]" :isRequired="true">
                <template v-slot:input>
                    <input type="text" id="expDate" v-model="renderStore.anetPayment.expDate" placeholder="MM/YY"
                        :maxLength="5" v-maska data-maska="##/##" @change="renderStore.tokenizePaymentMethod"
                        @blur="renderStore.tokenizePaymentMethod" />
                </template>
            </ValidatedInput>

            <ValidatedInput id="cardCode" :value="renderStore.anetPayment.cardCode" label="Security code"
                label-for="cardCode" name="cardCode" class="rd-w-full md:rd-flex-equal" :class="[{ 'rd-mt-4 md:rd-mt-0': fieldStyle != FieldStyle.Compact && !isModalDisplay },
                { 'rd-mt-2.5': (fieldStyle != FieldStyle.Compact && isModalDisplay) }]" :isRequired="true">
                <template v-slot:input>
                    <input type="text" id="cardCode" name="cardCode" v-model="renderStore.anetPayment.cardCode"
                        placeholder="CVC" :maxLength="4" @change="renderStore.tokenizePaymentMethod"
                        @blur="renderStore.tokenizePaymentMethod" />
                </template>
            </ValidatedInput>
        </div>
    </div>

    <div v-if="renderStore.gift.paymentMethodType === PaymentMethodType.ACH"
        :class="{ 'fields-compact': fieldStyle == FieldStyle.Compact }">
        <div class="form-row">
            <ValidatedInput id="routingNumber" :value="renderStore.anetPayment.routingNumber" label="Routing number"
                name="routingNumber" class="rd-w-full" :isRequired="true">
                <template v-slot:input>
                    <input type="text" v-model="renderStore.anetPayment.routingNumber" :maxLength="9"
                        @change="renderStore.tokenizePaymentMethod" @blur="renderStore.tokenizePaymentMethod" />
                </template>
            </ValidatedInput>
        </div>

        <div class="form-row" :class="[{ 'rd-mt-0 md:rd-gap-x-4 md:rd-mt-4': fieldStyle != FieldStyle.Compact && !isModalDisplay },
        { 'rd-mt-0 md:rd-gap-x-4': (fieldStyle != FieldStyle.Compact && isModalDisplay) }]">

            <ValidatedInput id="accountNumber" :value="renderStore.anetPayment.accountNumber" label="Account number"
                name="accountNumber" class="rd-w-full md:rd-flex-equal" :class="[{ 'rd-mt-4 md:rd-mt-0': fieldStyle != FieldStyle.Compact && !isModalDisplay },
                { 'rd-mt-2.5': (fieldStyle != FieldStyle.Compact && isModalDisplay) }]" :isRequired="true">
                <template v-slot:input>
                    <input type="text" v-model="renderStore.anetPayment.accountNumber" :maxLength="9"
                        @change="renderStore.tokenizePaymentMethod" @blur="renderStore.tokenizePaymentMethod" />
                </template>
            </ValidatedInput>

            <ValidatedInput id="accountType" :value="renderStore.anetPayment.accountType" label="Account Type"
                name="accountType" class="rd-w-full md:rd-flex-equal" :class="[{ 'rd-mt-4 md:rd-mt-0': fieldStyle != FieldStyle.Compact && !isModalDisplay },
                { 'rd-mt-2.5': (fieldStyle != FieldStyle.Compact && isModalDisplay) }]" :isRequired="true">
                <template v-slot:input>
                    <select id="accountType" v-model="renderStore.anetPayment.accountType"
                        @change="renderStore.tokenizePaymentMethod">
                        <option v-for="item in accountTypes" :key="item.value" :value="item.value">
                            {{ item.name }}
                        </option>
                    </select>
                </template>
            </ValidatedInput>
        </div>
    </div>
    <div id="authorizenet-element-errors">{{ renderStore.paymentElementError }}</div>
</template>
